import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import {
  MdOutlineMedicalServices,
  MdOutlineAssignment,
  MdOutlineDescription,
  MdKeyboardArrowRight,
  MdCheckCircle,
  MdEdit,
  MdRadioButtonUnchecked
} from 'react-icons/md';

export default function IntraOperative() {
  const { uhid_no } = useParams();

  const [formStatus, setFormStatus] = useState({
    intraOperativeNursing: 'not-started',
    operationSheet: 'not-started'
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkFormStatuses();
  }, [uhid_no]);

  const getFormStatus = (apiResponse, key) => {
    if (apiResponse.status === 'fulfilled') {
      const data = apiResponse.value.data;
      if (data && Object.keys(data).length > 0) return 'complete';
    }

    const savedForm = sessionStorage.getItem(`intraOpForm_${uhid_no}_${key}`);
    return savedForm ? 'incomplete' : 'not-started';
  };

  const checkFormStatuses = async () => {
    try {
      setLoading(true);

      const results = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/get_intra_op_record/${uhid_no}`),
        axios.get(`${API_BASE_URL}/get_operation_sheet/${uhid_no}`)
      ]);

      const newStatus = {
        intraOperativeNursing: getFormStatus(results[0], 'intraOperativeNursing'),
        operationSheet: getFormStatus(results[1], 'operationSheet')
      };

      setFormStatus(newStatus);
    } catch (error) {
      console.error('Error fetching intra-operative form statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <MdCheckCircle className="w-5 h-5 text-green-500" />;
      case 'incomplete':
        return <MdEdit className="w-5 h-5 text-yellow-500" />;
      default:
        return <MdRadioButtonUnchecked className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'complete':
        return 'Completed - Click to Edit';
      case 'incomplete':
        return 'In Progress - Continue';
      default:
        return 'Not Started';
    }
  };

  const getButtonStyle = (status) => {
    const base = "w-full py-8 text-lg flex items-center justify-between px-6 transition-all duration-200";
    switch (status) {
      case 'complete':
        return `${base} bg-green-600 hover:bg-green-500 text-white`;
      case 'incomplete':
        return `${base} bg-yellow-600 hover:bg-yellow-500 text-white`;
      default:
        return `${base} bg-teal-700 hover:bg-teal-600 text-white`;
    }
  };

  const formItems = [
    {
      key: 'intraOperativeNursing',
      title: 'Intra Operative Nursing Record',
      description: 'Nursing documentation during surgery',
      icon: MdOutlineAssignment,
      url: `/intraOperativeNursingRecord/${uhid_no}`
    },
    {
      key: 'operationSheet',
      title: 'Operation Sheet',
      description: 'Surgical procedure documentation',
      icon: MdOutlineDescription,
      url: `/operationSheet/${uhid_no}`
    }
  ];

  const completedCount = Object.values(formStatus).filter((s) => s === 'complete').length;
  const totalCount = Object.keys(formStatus).length;

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600">Loading form statuses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-teal-50 px-4">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-lg">
        <div className="flex flex-col items-center space-y-4">
          <MdOutlineMedicalServices className="w-20 h-20 text-teal-600" />
          <h1 className="text-2xl font-bold text-center text-teal-800">Intra Operative</h1>
          <p className="text-sm text-gray-600">UHID: {uhid_no}</p>
        </div>

        {/* Progress Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm text-gray-600">{completedCount}/{totalCount} Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-teal-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="flex flex-col space-y-4">
          {formItems.map((item) => {
            const IconComponent = item.icon;
            const status = formStatus[item.key];
            return (
              <Button
                key={item.key}
                onClick={() => window.location.href = item.url}
                className={getButtonStyle(status)}
                variant="default"
              >
                <div className="flex items-center space-x-4">
                  <IconComponent className="w-6 h-6" />
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{item.title}</span>
                    <span className="text-xs text-teal-100">{item.description}</span>
                    <span className="text-xs mt-1 opacity-80">{getStatusText(status)}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(status)}
                  <MdKeyboardArrowRight className="w-5 h-5" />
                </div>
              </Button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Status Legend:</h3>
          <div className="flex flex-col space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <MdCheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">Completed (Click to edit)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdEdit className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-600">In Progress (Continue form)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Not Started</span>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <Button
          onClick={checkFormStatuses}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>
    </div>
  );
}

